import { isUserOnline } from '../../common'
import { onWindowUnload } from '../../common/helpers/window'
import { getShellApiInstance } from '../../common/shell-api-helpers'
import type { BannerNotificationInstance, SnackbarNotificationInstance } from '../../services/display-notification'
import { getLaunchDarklyFlag } from '../../services/feature-flags'
import { LaunchDarklyVariations } from '../../services/feature-flags/models'
import { getTranslation } from '../../services/i18n/i18nUtils'
import { getEventBus } from '../../services/namespaces'
import type { NotificationChannelEvents } from '../../services/notification-channel'
import { NotificationChannelNamespace } from '../../services/notification-channel'
import type { ShellHook } from './models'

export const initializeNotificationController: ShellHook = async () => {
  let userWasOffline = false
  let userWasDisconnected = false
  let currentSnackbarNotification: SnackbarNotificationInstance | null = null
  let currentBannerNotification: BannerNotificationInstance | null = null
  const channelEvents = getEventBus().subscribeTo<
    typeof NotificationChannelNamespace,
    typeof NotificationChannelEvents
  >(NotificationChannelNamespace)

  const disconnectedHandler = () => {
    if (currentSnackbarNotification) {
      closeSnackbarNotifications(currentSnackbarNotification)
    }
    if (currentBannerNotification) {
      closeBannerNotification(currentBannerNotification)
    }
    userWasDisconnected = true
    // We only display a snackbar if the user has lost internet connection
    if (!isUserOnline()) {
      userWasOffline = true
      currentSnackbarNotification = showNoInternetConnectionSnackbar()
    }
    if (!getLaunchDarklyFlag(LaunchDarklyVariations.SHELL_TRACK_NC_DISCONNECT_BANNER)) {
      currentBannerNotification = showDisconnectedBanner()
    }
  }

  const connectedHandler = () => {
    if (currentSnackbarNotification) {
      closeSnackbarNotifications(currentSnackbarNotification)
    }
    if (currentBannerNotification) {
      closeBannerNotification(currentBannerNotification)
    }
    if (userWasDisconnected) {
      userWasDisconnected = false
      if (userWasOffline) {
        currentSnackbarNotification = showInternetConnectionRestoredSnackbar()
        userWasOffline = false
      } else {
        currentSnackbarNotification = showNotificationChannelConnectionRestoredSnackbar()
      }
    }
  }

  channelEvents.disconnected.addListener(disconnectedHandler)
  channelEvents.connected.addListener(connectedHandler)

  onWindowUnload(() => {
    channelEvents.disconnected.removeListener(disconnectedHandler)
    channelEvents.connected.removeListener(connectedHandler)
  })
}

const showNoInternetConnectionSnackbar = (): SnackbarNotificationInstance => {
  const noInternetConnectionSnackbar = getShellApiInstance().display.snackbar({
    title: getTranslation('No internet connection'),
    id: 'connection-lost',
    message: getTranslation('Check your network or hold on while we try to reconnect.'),
    actions: [
      {
        label: getTranslation('Dismiss'),
        handler() {
          closeSnackbarNotifications(noInternetConnectionSnackbar)
        },
      },
    ],
  })
  return noInternetConnectionSnackbar
}

const showInternetConnectionRestoredSnackbar = (): SnackbarNotificationInstance => {
  const internetConnectionRestoredSnackbar = getShellApiInstance().display.snackbar({
    title: getTranslation(`You're back online`),
    id: 'connection-restored',
    message: getTranslation('Everything should be working normally again.'),
    actions: [
      {
        label: getTranslation('Dismiss'),
        handler() {
          closeSnackbarNotifications(internetConnectionRestoredSnackbar)
        },
      },
    ],
    dataTest: 'internetConnectionRestoredSnackbar',
  })
  return internetConnectionRestoredSnackbar
}

const showNotificationChannelConnectionRestoredSnackbar = (): SnackbarNotificationInstance => {
  const notificationChannelConnectionRestoredSnackbar = getShellApiInstance().display.snackbar({
    title: getTranslation('Notifications are back'),
    id: 'connection-restored',
    message: getTranslation('Notifications and presence statuses are working again.'),
    actions: [
      {
        label: getTranslation('Dismiss'),
        handler() {
          closeSnackbarNotifications(notificationChannelConnectionRestoredSnackbar)
        },
      },
    ],
    dataTest: 'notificationChannelConnectionRestoredSnackbar',
  })
  return notificationChannelConnectionRestoredSnackbar
}

const showDisconnectedBanner = (): BannerNotificationInstance =>
  getShellApiInstance().display.banner({
    description: getTranslation('You seem to be offline. Trying to reconnect ...'),
    closable: false,
    variant: 'dark',
    id: 'shellDisconnectionBanner',
    target: window,
  })

const closeSnackbarNotifications = (currentSnackbarNotification: SnackbarNotificationInstance) => {
  currentSnackbarNotification.close()
}

const closeBannerNotification = (currentBannerNotification: BannerNotificationInstance) => {
  currentBannerNotification.close()
}
